import { clsx } from "clsx";
import { useLocation } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import useNavStore from "common/hooks/useNavStore";
import type { DetailedWorkspace, NavItemType } from "common/types";

import Banners from "../Banners";
import SubscriptionActivation from "../SubscriptionActivation";
import DesktopNav from "./DesktopNav";

import type { ReactNode } from "react";

interface DesktopLayoutProps {
  navItems: NavItemType[];
  children: ReactNode;
  workspace: DetailedWorkspace;
  isLocked: boolean;
}

export default function DesktopLayout({
  navItems,
  children,
  workspace,
  isLocked,
}: DesktopLayoutProps) {
  const { isNavExpanded, setIsNavExpanded } = useNavStore();
  const { pathname } = useLocation();

  const isSettingsPage = pathname.includes("/settings");

  return (
    <div className="overflow-hidden flex flex-col h-dvh bg-[linear-gradient(132deg,#fff_-4.58%,#f5f6f9_42.09%,#dce1ea_85.29%)]">
      <RenderIf condition={!isSettingsPage}>
        <DesktopNav
          isExpanded={isNavExpanded}
          setIsExpanded={setIsNavExpanded}
          navItems={navItems}
        />
      </RenderIf>

      <div
        className={clsx(
          "flex w-full",
          !isSettingsPage && [
            "transition-[padding] duration-300",
            isNavExpanded ? "pl-[216px]" : "pl-16",
          ],
        )}
      >
        <RenderIf condition={!isSettingsPage}>
          <button
            aria-label="expand"
            className={clsx(
              "w-2 h-full absolute z-10",
              isNavExpanded ? "cursor-w-resize" : "cursor-e-resize",
            )}
            type="button"
            onClick={() => setIsNavExpanded(!isNavExpanded)}
          />
        </RenderIf>

        {isLocked ? (
          <div className="h-dvh w-full">
            <SubscriptionActivation />
          </div>
        ) : (
          <>
            <section
              aria-label="Banners"
              className={clsx(
                "peer absolute w-full z-10 inset-x-0 pr-2 transition-[padding] duration-300 empty:hidden top-2",
                isNavExpanded ? "pl-[224px]" : "pl-[72px]",
              )}
            >
              {workspace && (
                <RenderIf condition={!isSettingsPage}>
                  <Banners />
                </RenderIf>
              )}
            </section>

            <main className="flex flex-col flex-1 h-dvh p-2 w-full relative peer-[:not(:empty)]:pt-14 pt-2">
              {children}
            </main>
          </>
        )}
      </div>
    </div>
  );
}
